(function () {
  "use strict";

  var DEFAULT_STYLE = "mapbox://styles/mapbox/streets-v12";
  var WORLD_CENTER = [0, 20];
  var WORLD_ZOOM = 1.5;

  function maskToken(token) {
    if (!token) {
      return "missing";
    }

    if (token.length <= 12) {
      return "present (" + token.length + " chars)";
    }

    return token.slice(0, 6) + "..." + token.slice(-4) + " (" + token.length + " chars)";
  }

  function createDebugger(root, enabled) {
    var panel = root.querySelector("[data-map-viewer-debug]");
    var log = root.querySelector("[data-map-viewer-debug-log]");

    function show(open) {
      if (!panel) {
        return;
      }

      panel.hidden = false;
      if (open) {
        panel.open = true;
      }
    }

    function write(level, message, data) {
      var item;
      var detail = "";

      if (data !== undefined) {
        try {
          detail = " " + JSON.stringify(data);
        } catch (error) {
          detail = " " + String(data);
        }
      }

      if (enabled || level === "error" || level === "warn") {
        show(level === "error" || level === "warn");
      }

      if (log && !panel.hidden) {
        item = document.createElement("li");
        item.setAttribute("data-level", level);
        item.textContent = new Date().toLocaleTimeString() + " [" + level + "] " + message + detail;
        log.appendChild(item);
      }

      if (window.console && window.console[level]) {
        window.console[level]("Map Focus Viewer: " + message, data || "");
      }
    }

    if (enabled) {
      show(true);
    }

    return {
      info: function (message, data) { write("info", message, data); },
      warn: function (message, data) { write("warn", message, data); },
      error: function (message, data) { write("error", message, data); }
    };
  }

  function readConfig(root, debug) {
    var configEl = root.querySelector("[data-map-viewer-config]");
    var config = {};

    if (!configEl) {
      debug.warn("config script element is missing");
      return config;
    }

    try {
      config = JSON.parse(configEl.textContent || "{}");
      debug.info("read config script", {
        tokenPresent: Boolean(config.tokenPresent),
        tokenLength: config.tokenLength,
        sectionId: config.sectionId
      });
    } catch (error) {
      debug.error("could not parse config script", error.message);
    }

    return config;
  }

  function getParam(params, name) {
    var value = params.get(name);
    return value === null ? "" : value.trim();
  }

  function parseNumber(value, name, min, max, debug) {
    if (value === "") {
      return null;
    }

    var number = Number(value);
    if (!Number.isFinite(number)) {
      debug.warn("invalid " + name + " value", value);
      return null;
    }

    if (number < min || number > max) {
      debug.warn(name + " is out of range", number);
      return null;
    }

    return number;
  }

  function parseBbox(value, debug) {
    if (!value) {
      return null;
    }

    var parts = value.split(",").map(function (part) {
      return Number(part.trim());
    });

    if (parts.length !== 4 || parts.some(function (part) { return !Number.isFinite(part); })) {
      debug.warn("bbox must be four comma-separated numbers: w,s,e,n", value);
      return null;
    }

    var bbox = {
      west: parts[0],
      south: parts[1],
      east: parts[2],
      north: parts[3]
    };

    if (bbox.west < -180 || bbox.west > 180 || bbox.east < -180 || bbox.east > 180) {
      debug.warn("bbox longitude values are out of range", value);
      return null;
    }

    if (bbox.south < -90 || bbox.south > 90 || bbox.north < -90 || bbox.north > 90) {
      debug.warn("bbox latitude values are out of range", value);
      return null;
    }

    if (bbox.west >= bbox.east || bbox.south >= bbox.north) {
      debug.warn("bbox must be ordered west,south,east,north", value);
      return null;
    }

    return bbox;
  }

  function formatNumber(value, digits) {
    return value === null ? "missing" : value.toFixed(digits);
  }

  function appendMeta(meta, label, value) {
    var item = document.createElement("div");
    var term = document.createElement("dt");
    var description = document.createElement("dd");

    item.className = "map-viewer__meta-item";
    term.textContent = label;
    description.textContent = value || "missing";

    item.appendChild(term);
    item.appendChild(description);
    meta.appendChild(item);
  }

  function showError(root, message) {
    var status = root.querySelector("[data-map-viewer-status]");
    var mapWrap = root.querySelector(".map-viewer__map-wrap");
    var error = document.createElement("div");

    if (status) {
      status.textContent = message;
    }

    error.className = "map-viewer__error";
    error.textContent = message;
    mapWrap.appendChild(error);
  }

  function createBboxFeature(bbox) {
    return {
      type: "Feature",
      properties: {},
      geometry: {
        type: "Polygon",
        coordinates: [[
          [bbox.west, bbox.south],
          [bbox.east, bbox.south],
          [bbox.east, bbox.north],
          [bbox.west, bbox.north],
          [bbox.west, bbox.south]
        ]]
      }
    };
  }

  function getMapSize(mapEl) {
    var canvas = mapEl ? mapEl.querySelector(".mapboxgl-canvas") : null;

    return {
      mapWidth: mapEl ? mapEl.clientWidth : 0,
      mapHeight: mapEl ? mapEl.clientHeight : 0,
      canvasWidth: canvas ? canvas.width : 0,
      canvasHeight: canvas ? canvas.height : 0,
      canvasClientWidth: canvas ? canvas.clientWidth : 0,
      canvasClientHeight: canvas ? canvas.clientHeight : 0
    };
  }

  function bindCenteredWheelZoom(map, mapEl, debug) {
    var zoomStep = 0.35;

    if (!mapEl) {
      return;
    }

    if (map.scrollZoom && map.scrollZoom.disable) {
      map.scrollZoom.disable();
    }

    mapEl.addEventListener("wheel", function (event) {
      var currentZoom;
      var nextZoom;
      var minZoom;
      var maxZoom;

      if (event.deltaY === 0) {
        return;
      }

      event.preventDefault();

      currentZoom = map.getZoom();
      minZoom = map.getMinZoom ? map.getMinZoom() : 0;
      maxZoom = map.getMaxZoom ? map.getMaxZoom() : 24;
      nextZoom = event.deltaY < 0 ? currentZoom + zoomStep : currentZoom - zoomStep;
      nextZoom = Math.max(minZoom, Math.min(maxZoom, nextZoom));

      map.easeTo({
        center: map.getCenter(),
        zoom: nextZoom,
        duration: 160,
        easing: function (t) { return t; }
      });

      debug.info("centered wheel zoom", {
        from: currentZoom,
        to: nextZoom
      });
    }, { passive: false });
  }

  function initViewer(root) {
    var params = new URLSearchParams(window.location.search);
    var debugEnabled = getParam(params, "debug") === "1" || getParam(params, "debug") === "true";
    var debug = createDebugger(root, debugEnabled);
    var config = readConfig(root, debug);
    var dataToken = root.getAttribute("data-mapbox-token") || "";
    var token = config.mapboxToken || dataToken;
    var style = config.mapStyle || root.getAttribute("data-map-style") || DEFAULT_STYLE;
    var lat = parseNumber(getParam(params, "lat"), "lat", -90, 90, debug);
    var lng = parseNumber(getParam(params, "lng"), "lng", -180, 180, debug);
    var zoom = parseNumber(getParam(params, "zoom"), "zoom", 0, 24, debug);
    var bboxRaw = getParam(params, "bbox");
    var bbox = parseBbox(bboxRaw, debug);
    var label = getParam(params, "label");
    var placeId = getParam(params, "place_id");
    var labelEl = root.querySelector("[data-map-viewer-label]");
    var metaEl = root.querySelector("[data-map-viewer-meta]");
    var mapEl = root.querySelector("[data-map-viewer-map]");
    var hasCenter = lat !== null && lng !== null;
    var mapOptions;
    var map;

    debug.info("initializing", {
      token: maskToken(token),
      dataToken: maskToken(dataToken),
      liquidTokenPresent: root.getAttribute("data-mapbox-token-present"),
      liquidTokenLength: root.getAttribute("data-mapbox-token-length"),
      style: style || DEFAULT_STYLE,
      mapboxglLoaded: Boolean(window.mapboxgl),
      mapboxglSupported: Boolean(window.mapboxgl && window.mapboxgl.supported && window.mapboxgl.supported()),
      mapElementFound: Boolean(mapEl),
      mapWidth: mapEl ? mapEl.clientWidth : 0,
      mapHeight: mapEl ? mapEl.clientHeight : 0
    });

    if (labelEl) {
      labelEl.textContent = label || "Map Focus Viewer";
    }

    if (metaEl) {
      metaEl.innerHTML = "";
      appendMeta(metaEl, "lat", formatNumber(lat, 6));
      appendMeta(metaEl, "lng", formatNumber(lng, 6));
      appendMeta(metaEl, "zoom", formatNumber(zoom, 2));
      appendMeta(metaEl, "bbox", bbox ? [
        bbox.west.toFixed(6),
        bbox.south.toFixed(6),
        bbox.east.toFixed(6),
        bbox.north.toFixed(6)
      ].join(",") : "missing");
      appendMeta(metaEl, "place_id", placeId || "missing");
    }

    if (!window.mapboxgl) {
      showError(root, "Mapbox GL JS did not load.");
      debug.error("mapboxgl is unavailable");
      return;
    }

    if (window.mapboxgl.supported && !window.mapboxgl.supported()) {
      showError(root, "Mapbox GL JS is not supported in this browser.");
      debug.error("mapboxgl reports this browser is unsupported");
      return;
    }

    if (!token) {
      showError(root, "Add a Mapbox token in the section settings.");
      debug.error("missing Mapbox token");
      return;
    }

    window.mapboxgl.accessToken = token;

    mapOptions = {
      container: mapEl,
      style: style || DEFAULT_STYLE,
      attributionControl: false,
      center: hasCenter ? [lng, lat] : WORLD_CENTER,
      zoom: hasCenter && zoom !== null ? zoom : WORLD_ZOOM
    };

    map = new window.mapboxgl.Map(mapOptions);
    if (map.dragPan && map.dragPan.disable) {
      map.dragPan.disable();
    }
    if (map.touchZoomRotate && map.touchZoomRotate.disableRotation) {
      map.touchZoomRotate.disableRotation();
    }
    if (map.touchZoomRotate && map.touchZoomRotate.disable) {
      map.touchZoomRotate.disable();
    }
    if (map.boxZoom && map.boxZoom.disable) {
      map.boxZoom.disable();
    }
    if (map.doubleClickZoom && map.doubleClickZoom.disable) {
      map.doubleClickZoom.disable();
    }
    if (map.keyboard && map.keyboard.disable) {
      map.keyboard.disable();
    }
    map.addControl(new window.mapboxgl.NavigationControl(), "top-right");
    map.addControl(new window.mapboxgl.AttributionControl({ compact: true }), "bottom-right");
    bindCenteredWheelZoom(map, mapEl, debug);

    function resizeMap(source) {
      var size = getMapSize(mapEl);

      if (!size.mapWidth || !size.mapHeight) {
        debug.warn("skipped map.resize() because container is zero-sized", {
          source: source,
          size: size
        });
        return;
      }

      debug.info("calling map.resize()", {
        source: source,
        size: size
      });
      map.resize();
    }

    window.requestAnimationFrame(function () {
      resizeMap("animation frame");
    });

    window.setTimeout(function () {
      resizeMap("250ms timeout");
    }, 250);

    window.setTimeout(function () {
      resizeMap("1000ms timeout");
    }, 1000);

    if (window.ResizeObserver) {
      new window.ResizeObserver(function () {
        resizeMap("resize observer");
      }).observe(mapEl);
    }

    map.on("styledata", function () {
      debug.info("styledata event");
    });

    map.on("load", function () {
      debug.info("map load event", {
        center: map.getCenter(),
        zoom: map.getZoom(),
        size: getMapSize(mapEl)
      });

      if (bbox) {
        map.addSource("map-viewer-bbox", {
          type: "geojson",
          data: createBboxFeature(bbox)
        });

        map.addLayer({
          id: "map-viewer-bbox-fill",
          type: "fill",
          source: "map-viewer-bbox",
          paint: {
            "fill-color": "#00a39b",
            "fill-opacity": 0.14
          }
        });

        map.addLayer({
          id: "map-viewer-bbox-outline",
          type: "line",
          source: "map-viewer-bbox",
          paint: {
            "line-color": "#005f62",
            "line-width": 2
          }
        });

        if (!hasCenter) {
          debug.info("fitting map to bbox");
          map.fitBounds([[bbox.west, bbox.south], [bbox.east, bbox.north]], {
            padding: 40,
            duration: 0
          });
        }
      }
    });

    map.on("idle", function () {
      debug.info("map idle event", {
        loaded: map.loaded(),
        center: map.getCenter(),
        zoom: map.getZoom(),
        size: getMapSize(mapEl)
      });
    });

    map.on("error", function (event) {
      var error = event && event.error ? event.error : event;
      var message = error && error.message ? error.message : "Mapbox error";
      debug.error(message, {
        status: error && error.status,
        url: error && error.url
      });
    });
  }

  function init() {
    document.querySelectorAll("[data-map-viewer]").forEach(initViewer);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
